import React, { FC } from 'react';
import Moment from 'moment';
import { DATE_FORMAT } from '@Constants/constants';
import './HistoryRow.scss';

export interface HistoryItem {
    id: number;
    nameOld?: string;
    nameNew?: string;
    startDateOld?: string | null;
    startDateNew?: string | null;
    statusOld?: string;
    statusNew?: string;
    targetDateOld?: string | null;
    targetDateNew?: string | null;
    responsibleUserOld?: string;
    responsibleUserNew?: string;
    targetDaysOutOld?: number | null;
    targetDaysOutNew?: number | null;
    additionalInfoOld?: string;
    additionalInfoNew?: string;
    budgetOld?: number | null;
    budgetNew?: number | null;
    actualOld?: number | null;
    actualNew?: number | null;
    createdBy: string;
    created: string | null;
}

interface HistoryRowProps {
    historyItem: HistoryItem
}

const HistoryRow: FC<HistoryRowProps> = ({ historyItem }) => {
    const historyTemplate = (): {
        displayName: string;
        oldData: string | number | undefined;
        newData: string | number | undefined;
    }[] => {
        const itemsToDisplay: {
            displayName: string;
            oldData: string | number | undefined;
            newData: string | number | undefined;
        }[] = [];

        const additionalInfoOld = historyItem.additionalInfoOld ?? "";
        const additionalInfoNew = historyItem.additionalInfoNew ?? "";

        if (additionalInfoOld !== additionalInfoNew) {
            itemsToDisplay.push({
                displayName: 'Additional Information',
                oldData: additionalInfoOld,
                newData: additionalInfoNew,
            });
        }

        if (historyItem.nameOld !== historyItem.nameNew) {
            itemsToDisplay.push({
                displayName: 'Name',
                oldData: historyItem.nameOld,
                newData: historyItem.nameNew,
            });
        }

        if (historyItem.startDateOld !== historyItem.startDateNew) {
            itemsToDisplay.push({
                displayName: 'Start Date',
                oldData: historyItem.startDateOld ? Moment.utc(historyItem.startDateOld).format(DATE_FORMAT) : "",
                newData: historyItem.startDateNew ? Moment.utc(historyItem.startDateNew).format(DATE_FORMAT) : "",
            });
        }

        if (historyItem.statusOld !== historyItem.statusNew) {
            itemsToDisplay.push({
                displayName: 'Status',
                oldData: historyItem.statusOld,
                newData: historyItem.statusNew,
            });
        }

        if (historyItem.targetDateOld !== historyItem.targetDateNew) {
            itemsToDisplay.push({
                displayName: 'Target Date',
                oldData: historyItem.targetDateOld ? Moment.utc(historyItem.targetDateOld).format(DATE_FORMAT) : "",
                newData: historyItem.targetDateNew ? Moment.utc(historyItem.targetDateNew).format(DATE_FORMAT) : "",
            });
        }

        if (historyItem.responsibleUserOld !== historyItem.responsibleUserNew) {
            itemsToDisplay.push({
                displayName: 'Responsible User',
                oldData: historyItem.responsibleUserOld,
                newData: historyItem.responsibleUserNew,
            });
        }

        if (historyItem.targetDaysOutOld !== historyItem.targetDaysOutNew) {
            itemsToDisplay.push({
                displayName: 'Target Days Out',
                oldData: historyItem.targetDaysOutOld,
                newData: historyItem.targetDaysOutNew,
            });
        }

        const budgetOld = historyItem.budgetOld ?? 0;
        const budgetNew = historyItem.budgetNew ?? 0;
        const actualOld = historyItem.actualOld ?? 0;
        const actualNew = historyItem.actualNew ?? 0;

        if (budgetOld !== budgetNew) {
            itemsToDisplay.push({
                displayName: 'Budget',
                oldData: budgetOld,
                newData: budgetNew,
            });
        }

        if (actualOld !== actualNew) {
            itemsToDisplay.push({
                displayName: 'Actual',
                oldData: actualOld,
                newData: actualNew,
            });
        }

        return itemsToDisplay;
    };

    const changedTemplate = (): string => {
        if (historyItem.id === 0) {
            return ' created task.';
        }

        return ' changed ';
    };

    return (
        <div className='location-task-history-row'>
            <div>
                <span className='person-bubble'>
                    {historyItem.createdBy}
                </span>
                {changedTemplate()}
                {historyTemplate().map((item, index) =>
                    <span key={index}>
                        {index ? ', ' : ''}
                        <strong>{item.displayName}</strong> from <strong>{item.oldData}</strong> to <strong>{item.newData}</strong>
                    </span>
                )}
            </div>
            <div className='history-date'>
                {Moment.utc(historyItem.created).format(DATE_FORMAT)}
            </div>
        </div>
    );
};

export default HistoryRow;
